import type { RuleAction, SetRule } from '@kkp-fraud/shared';
import type { RuleSetDraft } from '../hooks/useRuleSetDraft';
import { t } from '../i18n';
import { ACTIONS } from '../fields';
import { COUNTABLE_ACTIONS, DEFAULT_COUNT_ACTIONS, countedRules, newSetRule, setRuleCandidates, withSetRules } from '../ruleSets';
import { Tip } from './PageHead';
import { ProblemList } from './RuleCheckView';
import { fieldPath, type CheckedProblem } from '../ruleCheck';

/** Inside a set rule's own card, a problem's place starts at the card: `setRules[0].minHits` → "At least". */
const inCard = (p: CheckedProblem) => fieldPath({ ...p, path: p.path.replace(/^setRules\[\d+\]\.?/, '') });

/**
 * 2c rule sets (a) — the set-level rules of the set on screen: "at least N rules of this set
 * fired on one transaction → an action". Evaluated by the engine after the set's own rules;
 * a fire is a hit like any other (its `rule_id` is the set rule's id) and its action takes part in
 * the decision like any rule's. A set rule's `points` is not shown and never changed here (no
 * scoring since 30 Sep 2026); a new one gets the template's number (`newSetRule`).
 *
 * Every edit goes into the draft (`setRules`), exactly like a rule edit: the version line's
 * draft chip then offers Publish. The engine checks the draft (`useRuleCheck`); its problems
 * under `setRules[i]` are listed with the row they are about — the words come from the
 * catalogue by code, as everywhere else.
 */
export function SetRulesEditor({ state, readOnly = false }: { state: RuleSetDraft; readOnly?: boolean }) {
  const draft = state.draft;
  if (!draft) return null;
  const rules = draft.setRules ?? [];
  const candidates = setRuleCandidates(draft);
  const write = (next: SetRule[]) => state.setDraft(withSetRules(draft, next));
  const patch = (i: number, change: Partial<SetRule>) =>
    write(
      rules.map((r, x) => {
        if (x !== i) return r;
        const next = { ...r, ...change };
        // a key set to `undefined` is removed, never stored as `undefined`
        for (const key of Object.keys(change) as (keyof SetRule)[]) if (change[key] === undefined) delete next[key];
        return next;
      }),
    );
  const problems = state.check.result?.problems ?? [];
  const problemsOf = (i: number) => problems.filter((p) => p.path === `setRules[${i}]` || p.path.startsWith(`setRules[${i}].`));
  const topLevel = problems.filter((p) => p.path === 'setRules');

  return (
    <div className="stack" data-testid="set-rules">
      <div className="row-wrap">
        <span className="small muted">{t('rulesets.setRules.lead')}</span>
        <Tip>{t('rulesets.setRules.tip')}</Tip>
        <span className="spacer" />
        {!readOnly && (
          <button type="button" className="btn small" onClick={() => write([...rules, newSetRule(rules.map((r) => r.id))])}>
            {t('rulesets.setRules.add')}
          </button>
        )}
      </div>
      {topLevel.length > 0 && <ProblemList problems={topLevel} />}
      {rules.length === 0 && <div className="empty">{t('rulesets.setRules.empty')}</div>}
      {rules.map((r, i) => {
        const own = problemsOf(i);
        /** The input a message is about is marked, as in the rule editor. */
        const marked = (field: string) => own.some((p) => inCard(p) === field);
        const counts = r.countActions ?? DEFAULT_COUNT_ACTIONS;
        const total = countedRules(r, draft);
        return (
          <section key={i} className={`card${own.length > 0 ? ' invalid' : ''}`} data-testid={`set-rule-${r.id}`}>
            <header className="card-head row-wrap">
              <label className="check small">
                <input
                  className="toggle"
                  type="checkbox"
                  role="switch"
                  aria-label={t('rulesets.setRules.enabled.a11y', { id: r.id })}
                  disabled={readOnly}
                  checked={r.enabled}
                  onChange={(e) => patch(i, { enabled: e.target.checked })}
                />
                <span className="mono">{r.id}</span>
              </label>
              <span className="spacer" />
              {!readOnly && (
                <button
                  type="button"
                  className="btn small ghost"
                  aria-label={t('rulesets.setRules.remove.a11y', { id: r.id })}
                  onClick={() => write(rules.filter((_, x) => x !== i))}
                >
                  ✕
                </button>
              )}
            </header>
            <div className="card-body stack">
              <div className="row-wrap">
                <label className="field">
                  {t('rulesets.setRules.id.label')}
                  <input
                    type="text"
                    className={`f-val mono${marked('id') ? ' invalid' : ''}`}
                    aria-label={t('rulesets.setRules.id.a11y', { n: i + 1 })}
                    disabled={readOnly}
                    value={r.id}
                    onChange={(e) => patch(i, { id: e.target.value })}
                  />
                </label>
                <label className="field grow">
                  {t('rulesets.setRules.name.label')}
                  <input
                    type="text"
                    className={marked('name') ? 'invalid' : undefined}
                    aria-label={t('rulesets.setRules.name.a11y', { id: r.id })}
                    disabled={readOnly}
                    value={r.name}
                    onChange={(e) => patch(i, { name: e.target.value })}
                  />
                </label>
              </div>
              <div className="row-wrap">
                <label className="field">
                  {t('rulesets.setRules.minHits.label')}
                  <input
                    type="number"
                    min={1}
                    max={Math.max(1, total)}
                    className={`num-input${marked('minHits') ? ' invalid' : ''}`}
                    aria-label={t('rulesets.setRules.minHits.a11y', { id: r.id })}
                    disabled={readOnly}
                    value={Number.isFinite(r.minHits) ? r.minHits : ''}
                    onChange={(e) => patch(i, { minHits: e.target.value === '' ? Number.NaN : Number(e.target.value) })}
                  />
                </label>
                <span className="small muted">{t('rulesets.setRules.of.count', { n: total })}</span>
                <label className="field">
                  {t('rulesets.setRules.action.label')}
                  <select
                    aria-label={t('rulesets.setRules.action.a11y', { id: r.id })}
                    disabled={readOnly}
                    value={r.action}
                    onChange={(e) => patch(i, { action: e.target.value as RuleAction })}
                  >
                    {ACTIONS.map((a) => (
                      <option key={a} value={a}>
                        {a}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <fieldset className="stack">
                <legend className="small">{t('rulesets.setRules.of.label')}</legend>
                <label className="check small">
                  <input
                    type="checkbox"
                    aria-label={t('rulesets.setRules.of.all.a11y', { id: r.id })}
                    disabled={readOnly}
                    checked={r.of === undefined}
                    onChange={(e) => patch(i, { of: e.target.checked ? undefined : candidates.map((c) => c.id) })}
                  />
                  {t('rulesets.setRules.of.all')}
                </label>
                {r.of !== undefined && (
                  <div className="row-wrap">
                    {candidates.map((c) => (
                      <label key={c.id} className="check small">
                        <input
                          type="checkbox"
                          disabled={readOnly}
                          checked={r.of!.includes(c.id)}
                          onChange={(e) => patch(i, { of: e.target.checked ? [...r.of!, c.id] : r.of!.filter((x) => x !== c.id) })}
                        />
                        <span className="mono">{c.id}</span>
                      </label>
                    ))}
                    {/* An id this set no longer has — shown so it can be taken out (the engine refuses it). */}
                    {r.of
                      .filter((id) => !candidates.some((c) => c.id === id))
                      .map((id) => (
                        <label key={id} className="check small invalid">
                          <input type="checkbox" disabled={readOnly} checked onChange={() => patch(i, { of: r.of!.filter((x) => x !== id) })} />
                          <span className="mono">{id}</span>
                        </label>
                      ))}
                  </div>
                )}
              </fieldset>

              <fieldset className="stack">
                <legend className="small">{t('rulesets.setRules.countActions.label')}</legend>
                <label className="check small">
                  <input
                    type="checkbox"
                    aria-label={t('rulesets.setRules.countActions.default.a11y', { id: r.id })}
                    disabled={readOnly}
                    checked={r.countActions === undefined}
                    onChange={(e) => patch(i, { countActions: e.target.checked ? undefined : [...DEFAULT_COUNT_ACTIONS] })}
                  />
                  {t('rulesets.setRules.countActions.default')}
                </label>
                <div className="row-wrap">
                  {COUNTABLE_ACTIONS.map((a) => (
                    <label key={a} className="check small">
                      <input
                        type="checkbox"
                        disabled={readOnly || r.countActions === undefined}
                        checked={counts.includes(a)}
                        onChange={(e) =>
                          patch(i, { countActions: e.target.checked ? [...counts, a] : counts.filter((x) => x !== a) })
                        }
                      />
                      {a}
                    </label>
                  ))}
                </div>
              </fieldset>

              <p className="small" data-testid={`set-rule-sentence-${r.id}`}>
                {t('rulesets.setRules.sentence', { n: Number.isFinite(r.minHits) ? r.minHits : '?', total, action: r.action })}
              </p>
              {own.length > 0 && <ProblemList problems={own} pathOf={inCard} />}
            </div>
          </section>
        );
      })}
    </div>
  );
}
